import {useRespondToPermissionUpgradeRequestMutation, useUpdateCompanyMemberPermissionMutation} from "@/api/companyApi";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import {useUserId, useUserPermission} from "@/hooks/useUserHooks";
import {AbilityContext} from "@/permissions";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {PERMISSIONS} from "@/store/constants";
import type {TAccountMember, TPermission} from "@/store/types";
import {useContext, type FC} from "react";
import {useTranslation} from "react-i18next";

const UpdateUserPermission: FC<{
  companyId: number;
  companyMember: TAccountMember;
  permission: TPermission;
  onMakeOwner?: () => void;
}> = ({companyId, companyMember, permission, onMakeOwner}) => {
  const {t} = useTranslation("multi_account");
  const abilityContext = useContext(AbilityContext);
  const loggedInUserId = useUserId();
  const isViewingSelf = companyMember.user.id === loggedInUserId;
  const userPermission = useUserPermission({companyId, excludeAdmins: isViewingSelf});
  const isCompanyOwner = userPermission === "ADMIN";
  const canUpdateMembers = abilityContext.can("update", "CompanyMembers");

  const {mutateAsync: updatePermission} = useUpdateCompanyMemberPermissionMutation({
    companyMemberUserId: companyMember.user.id,
    companyId,
  });
  const {mutate: upgradePermission} = useRespondToPermissionUpgradeRequestMutation({companyId});

  return (
    <PermissionSelect
      value={permission}
      onChange={async value => {
        if (value === "MAKE_OWNER") {
          onMakeOwner?.();
          return;
        }

        if (!canUpdateMembers || value === permission) {
          return;
        }

        await updatePermission({
          newPermission: value as TPermission,
        });

        capturePostHogEvent(PostHogEvents.UserRoleChanged, {
          companyId,
          oldRole: permission,
          newRole: value,
          targetUserId: companyMember.user.id,
        });

        const permissionRequestStatus = companyMember.permission_request?.status;
        const requestedPermission = companyMember.permission_request?.permission_type;

        if (!!requestedPermission && permissionRequestStatus === "PENDING" && requestedPermission === value) {
          upgradePermission({
            user_id: companyMember.user.id,
            status: "ACCEPTED",
          });
        }
      }}
      excludedPermissions={
        !canUpdateMembers || (!isCompanyOwner && isViewingSelf)
          ? PERMISSIONS.filter(permission => permission !== userPermission)
          : []
      }
      topOptions={onMakeOwner ? [{value: "MAKE_OWNER", label: t("make_owner")}] : []}
      containerClassName="max-w-46.25"
    />
  );
};

export default UpdateUserPermission;
